import React, {
  useCallback,
  useRef,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import classNames from "classnames";
import { Textfield, Select, Option } from "@natera/form";
import {
  Corner,
  MDCMenuSurfaceFoundation,
  MenuItem,
  MenuSurface as BaseMenuSurface,
  MenuWidth,
} from "@natera/material/lib/menu";
import { useAddressAutocomplete } from "@app/hooks/useAddressAutocomplete";
import { MpxAddressSuggestion } from "@app/service/mpx";
import {
  AddressCompareFields,
  addressesMatch,
} from "@app/utils/addressesMatch";
import {
  GetValidationError,
  SetValidationError,
} from "@natera/platform/lib/hooks/useError";
import { FormField, Notification } from "@app/components";
import { ConfigContext } from "@app/provider";
import { HeapPHIDecorator } from "@app/components/analytics";
import { messages as profileFormAccordionMessages } from "../profileFormAccordion/messages";
import { messages as addressFormMessages } from "../addressForm/messages";
import STATES from "@etc/states.json";
import { Spinner } from "@natera/material/lib/progress";

import "./mpxAddressSuggestionForm.scss";

export const messages = defineMessages({
  mpxAddressFormCountry: {
    id: "mpxAddressFormCountry",
    defaultMessage: "Country/Region",
  },
  mpxAddressFormCountryValueUS: {
    id: "mpxAddressFormCountryValueUS",
    defaultMessage: "United States",
  },
  mpxAddressFormAddressLabel: {
    id: "mpxAddressFormAddressLabel",
    defaultMessage: "Address",
  },
  mpxAddressFormAddressNotFoundError: {
    id: "mpxAddressFormAddressNotFoundError",
    defaultMessage:
      "We’re unable to verify the street address as entered. This can happen if the address is new. Please review before saving.",
  },
  mpxAddressFormNoMatchesFound: {
    id: "mpxAddressFormNoMatchesFound",
    defaultMessage: "No matches found",
  },
  mpxAddressFormSuggestionLimit: {
    id: "mpxAddressFormSuggestionLimit",
    defaultMessage:
      "Showing only the first {pageLimit} results. Type more to refine the search.",
  },
  mpxAddressFormLoadingState: {
    id: "mpxAddressFormLoadingState",
    defaultMessage: "Loading",
  },
});

interface MpxAddressSuggestionFormProps {
  street1: string;
  onStreet1Change: (value: string) => void;
  apartmentUnit: string;
  onApartmentUnitChange: (apartmentUnit: string) => void;
  city: string;
  onCityChange: (city: string) => void;
  state: string;
  onStateChange: (state: string) => void;
  zipCode: string;
  onZipCodeChange: (zipCode: string) => void;
  getValidationError: GetValidationError;
  setValidationError: SetValidationError;
  className?: string;
}

const MpxAddressSuggestionForm: React.FC<MpxAddressSuggestionFormProps> = ({
  street1,
  onStreet1Change,
  apartmentUnit,
  onApartmentUnitChange,
  city,
  onCityChange,
  state,
  onStateChange,
  getValidationError,
  setValidationError,
  zipCode,
  onZipCodeChange,
  className,
}) => {
  const intl = useIntl();
  const { config } = useContext(ConfigContext);

  const {
    results,
    getPlace,
    clearResults,
    suggestionsLoading,
    lastSuggestReturnedEmpty,
    fetchSuggestions,
    selectedAddress,
    setSelectedAddress,
  } = useAddressAutocomplete();

  const textfieldRef = useRef<HTMLDivElement>(null);
  const surfaceFoundationRef = useRef<MDCMenuSurfaceFoundation>(null);
  const hasSearchedThisSessionRef = useRef(false);
  const ignoreNextBlurFromMenuRef = useRef(false);
  const initialAddressRef = useRef<AddressCompareFields | null>(null);
  const [selectedState, setSelectedState] = useState<string | undefined>(state);
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    setSelectedState(state);
  }, [state]);

  // save initial prefilled address so we can show warning when user changes any field
  useEffect(() => {
    if (initialAddressRef.current !== null) return;
    const hasPrefilledValue = [street1, city, state, zipCode].some(
      (val) => (val ?? "").trim() !== ""
    );
    if (hasPrefilledValue) {
      initialAddressRef.current = {
        street1: (street1 ?? "").trim(),
        city: (city ?? "").trim(),
        state: state ?? "",
        zipCode: zipCode ?? "",
        countryCode: "US",
      };
    }
  }, []);

  useEffect(() => {
    const street1Input = document.getElementById("street1");
    street1Input?.focus();
  }, []);

  const handleStreet1Change = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const val = e.target.value;

    onStreet1Change(val);

    // Clear validation error when user types
    if (getValidationError("street1")) {
      setValidationError("street1", "");
    }

    if (val.trim().length > 0) {
      hasSearchedThisSessionRef.current = true;
    }
    fetchSuggestions(val);
  };

  const evaluateAddressWarning = useCallback(
    (overrides?: { state?: string }) => {
      // do not show warning if street1 is empty
      if ((street1 ?? "").trim() === "") {
        setShowAddressWarning(false);
        return;
      }

      const currentState = overrides?.state ?? state;
      const newValue: AddressCompareFields = {
        city: city ?? "",
        countryCode: "US",
        zipCode: zipCode ?? "",
        state: currentState ?? "",
        street1: street1 ?? "",
      };

      if (selectedAddress) {
        const initialValue: AddressCompareFields = {
          city: selectedAddress?.city ?? "",
          countryCode: "US",
          zipCode: selectedAddress?.zipCode ?? "",
          state: selectedAddress?.state ?? "",
          street1: selectedAddress?.street1 ?? "",
        };
        if (!addressesMatch(initialValue, newValue)) {
          setShowAddressWarning(true);
        }
      } else if (initialAddressRef.current) {
        if (!addressesMatch(initialAddressRef.current, newValue)) {
          setShowAddressWarning(true);
        }
      } else if (hasSearchedThisSessionRef.current) {
        setShowAddressWarning(true);
      }
    },
    [selectedAddress, street1, city, state, zipCode]
  );

  const handleStreet1Blur = (
    e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const relatedTarget = e?.relatedTarget as Node | null | undefined;
    const menuEl = document.getElementById("street1-suggestions-list");
    const focusMovedToMenu =
      relatedTarget &&
      (textfieldRef.current?.contains(relatedTarget) ||
        menuEl?.contains(relatedTarget));

    if (street1.trim().length === 0) {
      setShowAddressWarning(false);
      return;
    }
    if (focusMovedToMenu || ignoreNextBlurFromMenuRef.current) {
      ignoreNextBlurFromMenuRef.current = false;
      return;
    }
    evaluateAddressWarning();
  };

  const showNoMatches =
    lastSuggestReturnedEmpty && !suggestionsLoading && results.length === 0;

  const DISPLAY_LIMIT = 3;
  const displayedResults = results.slice(0, DISPLAY_LIMIT);
  const showPageLimitRow = results.length > DISPLAY_LIMIT;
  const isMenuOpen = suggestionsLoading || results.length > 0 || showNoMatches;
  const selectableCount = showNoMatches
    ? 1
    : displayedResults.length + (showPageLimitRow ? 1 : 0);

  useEffect(() => {
    // No option highlighted when results appear/change; user must press Arrow Down/Up first.
    setHighlightedIndex(-1);
  }, [selectableCount, results, suggestionsLoading, showNoMatches]);

  const handleStreet1KeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isMenuOpen || selectableCount === 0) {
      if (e.key === "Escape" && surfaceFoundationRef.current) {
        surfaceFoundationRef.current.close();
        clearResults();
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const nextIndex =
          highlightedIndex < 0
            ? 0
            : Math.min(highlightedIndex + 1, selectableCount - 1);
        setHighlightedIndex(nextIndex);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const nextIndex = highlightedIndex <= 0 ? 0 : highlightedIndex - 1;
        setHighlightedIndex(nextIndex);
        break;
      }
      case "Enter":
        e.preventDefault();
        // Only select when highlighting a real address option (not page-limit row nor no-matches row).
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < displayedResults.length &&
          displayedResults[highlightedIndex]
        ) {
          handleAddressSelect(displayedResults[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        if (surfaceFoundationRef.current) {
          surfaceFoundationRef.current.close();
        }
        clearResults();
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const shouldOpen =
      suggestionsLoading || results.length > 0 || showNoMatches;
    if (surfaceFoundationRef.current) {
      if (shouldOpen) {
        surfaceFoundationRef.current.open();
      } else {
        surfaceFoundationRef.current.close();
      }
    }
  }, [suggestionsLoading, results.length, showNoMatches]);

  const handleAddressSelect = async (suggestion: MpxAddressSuggestion) => {
    setShowAddressWarning(false);
    clearResults();
    if (surfaceFoundationRef.current) {
      surfaceFoundationRef.current.close();
    }

    try {
      const addressDetails = await getPlace(suggestion.id);

      if (addressDetails) {
        setSelectedAddress(addressDetails);

        // Auto-populate street1
        if (addressDetails.street1) {
          onStreet1Change(addressDetails.street1);
        }

        // Auto-populate apartmentUnit (clear it)
        onApartmentUnitChange("");

        // Auto-populate city
        if (addressDetails.city) {
          onCityChange(addressDetails.city);
        }

        // Auto-populate state
        if (addressDetails.state) {
          onStateChange(addressDetails.state);
          setSelectedState(addressDetails.state);
        }

        // Auto-populate zipCode
        if (addressDetails.zipCode) {
          onZipCodeChange(addressDetails.zipCode);
        }

        // Clear validation error on successful selection
        setValidationError("street1", "");
      }
    } catch (error) {
      console.error("Error selecting address:");
    }
  };

  const handleStateChange = (value: string) => {
    setSelectedState(value);
    onStateChange(value);
    evaluateAddressWarning({ state: value });
  };

  const menuLoading = (
    <>
      <MenuItem key="loading" disabled>
        <div className="mpx-address-form__address-suggestions__loading">
          <Spinner
            indeterminate
            className="mpx-address-form__address-suggestions__spinner"
          />

          <span className="mpx-address-form__address-suggestions__loading-text">
            {intl.formatMessage(messages.mpxAddressFormLoadingState)}
          </span>
        </div>
      </MenuItem>
    </>
  );

  const menuItems = (
    <>
      {displayedResults.map((item, index) => (
        <MenuItem
          key={item.id}
          id={`street1-suggestion-${index}`}
          className={
            index === highlightedIndex
              ? "mpx-address-form__address-suggestions__option--highlighted"
              : undefined
          }
          onClick={() => handleAddressSelect(item)}
        >
          <span className="mpx-address-form__address-suggestions__option">
            {item.address}
          </span>
        </MenuItem>
      ))}
      {showPageLimitRow && (
        <MenuItem
          key="street1-page-limit"
          id={`street1-suggestion-${displayedResults.length}`}
          className={
            highlightedIndex === displayedResults.length
              ? "mpx-address-form__address-suggestions__option--highlighted"
              : undefined
          }
          onMouseDown={(e) => {
            e.preventDefault();
            ignoreNextBlurFromMenuRef.current = true;
          }}
          onClick={() => {}}
        >
          <span className="mpx-address-form__address-suggestions__option mpx-address-form__address-suggestions__option--message">
            {intl.formatMessage(messages.mpxAddressFormSuggestionLimit, {
              pageLimit: DISPLAY_LIMIT,
            })}
          </span>
        </MenuItem>
      )}
    </>
  );

  const noMatchesFound = (
    <MenuItem
      key="no-matches"
      id="street1-suggestion-0"
      className={
        showNoMatches && highlightedIndex === 0
          ? "mpx-address-form__address-suggestions__option--highlighted"
          : undefined
      }
      onMouseDown={(e) => {
        e.preventDefault();
        ignoreNextBlurFromMenuRef.current = true;
      }}
      onClick={() => {}}
    >
      <span className="mpx-address-form__address-suggestions__option">
        {intl.formatMessage(messages.mpxAddressFormNoMatchesFound)}
      </span>
    </MenuItem>
  );

  const menuContent = useMemo(() => {
    if (suggestionsLoading) return menuLoading;
    if (results.length > 0) return menuItems;
    if (lastSuggestReturnedEmpty) return noMatchesFound;
    return null;
  }, [suggestionsLoading, results, lastSuggestReturnedEmpty, highlightedIndex]);

  const onStreet1Clear = () => {
    setShowAddressWarning(false);
    onStreet1Change("");
    clearResults();
    if (surfaceFoundationRef.current) {
      surfaceFoundationRef.current.close();
    }
  };

  return (
    <div className={classNames("mpx-address-form", className)}>
      {showAddressWarning && (
        <Notification
          className="mpx-address-form__warning-notification"
          key="warning-address-not-found-id"
          type="note"
        >
          {intl.formatMessage(messages.mpxAddressFormAddressNotFoundError)}
        </Notification>
      )}

      <FormField
        label={intl.formatMessage(messages.mpxAddressFormCountry)}
        required
        htmlFor="country-select"
        withPadding
        className={"mpx-address-form__country-field"}
      >
        <HeapPHIDecorator protectAttr={["value"]}>
          <Select
            id="country-select"
            outlined
            required
            disabled
            name="country"
            data-testid="country"
            role="combobox"
            aria-labelledby="country-select"
            value="US"
            floating={true}
          >
            <Option value="US">
              {intl.formatMessage(messages.mpxAddressFormCountryValueUS)}
            </Option>
          </Select>
        </HeapPHIDecorator>
      </FormField>

      <FormField
        label={intl.formatMessage(messages.mpxAddressFormAddressLabel)}
        required
        error={getValidationError("street1")}
        htmlFor="street1"
        withPadding
      >
        <div
          ref={textfieldRef}
          className="mpx-address-form__address-suggestions"
        >
          <Textfield
            outline
            value={street1}
            onChange={handleStreet1Change}
            onBlur={(e) => handleStreet1Blur(e)}
            onKeyDown={handleStreet1KeyDown}
            aria-expanded={isMenuOpen}
            aria-autocomplete="list"
            aria-controls="street1-suggestions-list"
            aria-activedescendant={
              highlightedIndex >= 0 && results[highlightedIndex]
                ? `street1-suggestion-${highlightedIndex}`
                : undefined
            }
            aria-invalid={!!getValidationError("street1")}
            aria-describedby={
              getValidationError("street1") ? "street1-error" : undefined
            }
            placeholder={intl.formatMessage(
              addressFormMessages.addressFormStreetAddressPlaceholder
            )}
            name="street1"
            id="street1"
            data-testid="street1"
            clearable
            onClear={onStreet1Clear}
          />

          <BaseMenuSurface
            id="street1-suggestions-list"
            role="listbox"
            corner={Corner.BOTTOM_START}
            surfaceFoundationRef={surfaceFoundationRef}
            anchorRef={textfieldRef}
            menuWidth={MenuWidth.FLOATING_WIDTH}
          >
            {menuContent}
          </BaseMenuSurface>
        </div>
      </FormField>

      <FormField
        label={intl.formatMessage(addressFormMessages.addressFormApartmentUnit)}
        error={getValidationError("street2")}
        htmlFor="street2"
        withPadding
      >
        <HeapPHIDecorator protectAttr={["value"]}>
          <Textfield
            placeholder={intl.formatMessage(
              addressFormMessages.addressFormApartmentUnitPlaceholder
            )}
            id="street2"
            name="street2"
            data-testid="street2"
            value={apartmentUnit ?? ""}
            onChange={(e) =>
              onApartmentUnitChange && onApartmentUnitChange(e.target.value)
            }
            outline
            maxLength={config.TEXT_FIELD_MAX_LENGTH}
            aria-describedby={
              getValidationError("street2") ? "street2-error" : undefined
            }
          />
        </HeapPHIDecorator>
      </FormField>

      <FormField
        label={intl.formatMessage(addressFormMessages.addressFormCity)}
        error={getValidationError("city")}
        htmlFor="city"
        required
        withPadding
      >
        <HeapPHIDecorator protectAttr={["value"]}>
          <Textfield
            placeholder={intl.formatMessage(
              addressFormMessages.addressFormCityPlaceholder
            )}
            id="city"
            name="city"
            data-testid="city"
            value={city ?? ""}
            onChange={(e) => onCityChange && onCityChange(e.target.value)}
            onBlur={() => evaluateAddressWarning()}
            outline
            required
            maxLength={config.TEXT_FIELD_CITY_AND_COUNTRY_LENGTH}
            aria-describedby={
              getValidationError("city") ? "city-error" : undefined
            }
          />
        </HeapPHIDecorator>
      </FormField>

      <div className="mpx-address-form__state-zip-row">
        <FormField
          label={intl.formatMessage(addressFormMessages.addressFormState)}
          error={getValidationError("state")}
          required
          htmlFor="state-select"
          withPadding
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Select
              id="state-select"
              className={classNames("mpx-address-form__state-field", {
                "mpx-address-form__state-field--has-value": selectedState,
              })}
              outlined
              required
              name="state"
              data-testid="state"
              role="combobox"
              aria-labelledby="state-select"
              placeholder={intl.formatMessage(
                addressFormMessages.addressFormStatePlaceholder
              )}
              value={selectedState}
              onValueChange={handleStateChange}
              floating={true}
              aria-describedby={
                getValidationError("state") ? "state-error" : undefined
              }
            >
              {STATES.map((item) => (
                <Option key={item.abbreviation} value={item.abbreviation}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </HeapPHIDecorator>
        </FormField>

        <FormField
          label={intl.formatMessage(
            profileFormAccordionMessages.profileFormZipCode
          )}
          required
          error={getValidationError("zipCode")}
          htmlFor="zipCode"
          withPadding
        >
          <div id="zipCode-instructions" className="visually-hidden">
            {intl.formatMessage(
              profileFormAccordionMessages.profileFormZipTooltip
            )}
          </div>
          <HeapPHIDecorator protectAttr={["value"]} noWrap={true}>
            <Textfield
              placeholder={intl.formatMessage(
                profileFormAccordionMessages.profileFormZipCodePlaceholder
              )}
              id="zipCode"
              name="zipCode"
              data-testid="zipCode"
              autoComplete="postal-code"
              required
              outline
              aria-invalid={!!getValidationError("zipCode")}
              aria-describedby={
                getValidationError("zipCode")
                  ? "zipCode-instructions zipCode-error"
                  : "zipCode-instructions"
              }
              value={zipCode ?? ""}
              onChange={(e) =>
                onZipCodeChange && onZipCodeChange(e.target.value)
              }
              onBlur={() => evaluateAddressWarning()}
            />
          </HeapPHIDecorator>
        </FormField>
      </div>
    </div>
  );
};

export default MpxAddressSuggestionForm;
